import type { AnyRouter } from '@tanstack/react-router';
import { findExactRoute } from '@/routing/flat-routes';
import type { AppRouteMeta } from '@/routing/route-meta';
import { getAppRouteMeta } from '@/routing/router-static-data';
import { prettifyPath } from '@/routing/nav-from-router';
import { readStaticCrumb } from '@/routing/static-crumb';
import { routeAccessMeta } from './navigationTargets';
import { acceptedSearchParams } from './routeSearchParams';

/** The slice of a TanStack route the catalog reads (`getFlatRoutes(router)`). */
export type RouteForCatalog = {
  id?: unknown;
  fullPath?: unknown;
  options?: {
    staticData?: unknown;
    loader?: unknown;
    validateSearch?: unknown;
  };
  parentRoute?: unknown;
};

export interface AgentCatalogPage {
  /** What the page is called in the app: its sidebar label or breadcrumb. */
  label: string;
  /** In-app path. A record page's path is a pattern (`/workbooks/$workbookId`). */
  path: string;
  /** The breadcrumb trail above it ("Rebates › Reports"), when there is one. */
  section?: string;
  /** For a record page: the `$params` in `path` to fill with record ids. */
  params?: string[];
  /** The query params the page accepts (from its search validation). */
  query?: string[];
}

function isRoute(value: unknown): value is RouteForCatalog {
  return typeof value === 'object' && value !== null;
}

const crumbCache = new WeakMap<object, string | null>();

/** A route's static breadcrumb, read once per route. */
function crumbOf(route: RouteForCatalog): string | undefined {
  if (!crumbCache.has(route)) {
    crumbCache.set(route, readStaticCrumb(route.options?.loader));
  }
  return crumbCache.get(route) ?? undefined;
}

/** A route's display name: its sidebar label, else its breadcrumb. */
function nameOf(route: RouteForCatalog): string | undefined {
  return getAppRouteMeta(route)?.nav?.label ?? crumbOf(route);
}

/** The names of the routes above this one, outermost first. */
function sectionOf(
  route: RouteForCatalog,
  translate: (label: string) => string,
): string | undefined {
  const names: string[] = [];
  const seen = new Set<string>();
  let parent = route.parentRoute;
  while (isRoute(parent)) {
    const name = nameOf(parent);
    if (name && !seen.has(name)) {
      seen.add(name);
      names.unshift(translate(name));
    }
    parent = parent.parentRoute;
  }
  return names.length > 0 ? names.join(' › ') : undefined;
}

function toPage(
  route: RouteForCatalog & { fullPath: string },
  translate: (label: string) => string,
): AgentCatalogPage {
  const segments = route.fullPath.split('/').filter(Boolean);
  const params = segments
    .filter((segment) => segment.startsWith('$'))
    .map((segment) => segment.slice(1));
  const fixed = segments.filter((segment) => !segment.startsWith('$'));
  const name = params.length === 0 ? nameOf(route) : undefined;
  const label = name
    ? translate(name)
    : `${prettifyPath(`/${fixed.at(-1) ?? ''}`)}${params.length ? ' record' : ''}`;
  const section = sectionOf(route, translate);
  const query = acceptedSearchParams(route);
  return {
    label,
    path: route.fullPath,
    ...(section && { section }),
    ...(params.length > 0 && { params }),
    ...(query && query.length > 0 && { query }),
  };
}

/**
 * The route's path if the agent may offer it to this user: not the root or
 * the current page, not hidden, and with access rules (its own, inherited, or
 * its section's) that let the user in. A route with none is guarded only in
 * `beforeLoad`, so it can't be vetted here and isn't offered.
 */
function offerablePath(
  route: RouteForCatalog,
  routes: readonly RouteForCatalog[],
  options: { canAccess: (meta: AppRouteMeta) => boolean; currentPath?: string },
): string | null {
  if (typeof route.fullPath !== 'string') return null;
  const fullPath = route.fullPath.replace(/\/+$/, '') || '/';
  if (fullPath === '/' || fullPath === options.currentPath) return null;
  if (getAppRouteMeta(route)?.nav?.hidden) return null;
  const meta = routeAccessMeta(route, routes);
  return meta && options.canAccess(meta) ? fullPath : null;
}

/**
 * Every page the agent may send this user to, named the way the app names
 * it. Unlike the sidebar's catalog it includes record pages (`/x/$id`), as
 * patterns whose `$params` the agent fills with ids, and pages that inherit
 * their access rules from a parent route.
 */
export function buildAgentPageCatalog(
  routes: readonly RouteForCatalog[],
  options: {
    canAccess: (meta: AppRouteMeta) => boolean;
    translate: (label: string) => string;
    /** The current page, left out: the agent is already there. */
    currentPath?: string;
  },
): AgentCatalogPage[] {
  const byPath = new Map<string, AgentCatalogPage>();
  const namedPaths = new Set<string>();
  for (const route of routes) {
    const fullPath = offerablePath(route, routes, options);
    if (!fullPath) continue;
    // Index routes share their layout's path; keep the one with a name.
    const named = nameOf(route) !== undefined;
    if (!byPath.has(fullPath) || (named && !namedPaths.has(fullPath))) {
      byPath.set(fullPath, toPage({ ...route, fullPath }, options.translate));
      if (named) namedPaths.add(fullPath);
    }
  }
  return [...byPath.values()];
}

/**
 * The catalog entry for an agent-supplied path: an exact page, or the record
 * page whose pattern a concrete URL fills (`/workbooks/7c1d…` matches
 * `/workbooks/$workbookId`). Undefined for anything not in the catalog.
 */
export function findCatalogPage(
  router: Pick<AnyRouter, 'getMatchedRoutes'>,
  pages: readonly AgentCatalogPage[],
  href: string,
): AgentCatalogPage | undefined {
  const route = findExactRoute(router, href.split(/[?#]/)[0] ?? '');
  if (!route) return undefined;
  const path = route.fullPath.replace(/\/+$/, '') || '/';
  return pages.find((page) => page.path === path);
}
